import React from 'react';
import { X, Send } from 'lucide-react';
import api from '../config/api';

export function ComposeModal({ mailboxId, onClose, onSent }) {
  const draftKey = `composeDraft:${mailboxId || 'global'}`;

  const [formData, setFormData] = React.useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    bodyText: '',
  });
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [draftStatus, setDraftStatus] = React.useState(''); // '', 'saving', 'saved'
  const [savedAt, setSavedAt] = React.useState(null);

  // Attachments state
  const [attachments, setAttachments] = React.useState([]); // { tempId?, id?, name, progress, uploading, url }
  const fileInputRef = React.useRef(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  // Load draft on mount
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        setFormData(JSON.parse(saved));
        setDraftStatus('saved');
        setSavedAt(Date.now());
      }
    } catch (err) {
      // ignore
    }
  }, [draftKey]);

  // Autosave draft (debounced)
  React.useEffect(() => {
    setDraftStatus('saving');
    const id = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(formData));
        setDraftStatus('saved');
        setSavedAt(Date.now());
      } catch (err) {
        // ignore
      }
    }, 800);

    return () => clearTimeout(id);
  }, [formData, draftKey]);

  // cleanup pending timers on unmount
  React.useEffect(() => {
    return () => {
      if (pendingIntervalRef.current) {
        clearInterval(pendingIntervalRef.current);
        pendingIntervalRef.current = null;
      }
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    };
  }, []);

  function handleChange(e) {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  }

  // Attachment helpers
  function setAttachmentProgress(tempId, progress) {
    setAttachments(prev => prev.map(a => a.tempId === tempId ? { ...a, progress } : a));
  }

  function replaceAttachmentTemp(tempId, next) {
    setAttachments(prev => prev.map(a => a.tempId === tempId ? { ...a, ...next } : a));
  }

  async function uploadFile(file, tempId, controller) {
    const form = new FormData();
    form.append('file', file);

    try {
      const res = await api.post(`/api/webmail/${mailboxId}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setAttachmentProgress(tempId, percent);
        },
        signal: controller.signal,
      });

      // Expecting response: { id, filename, url }
      replaceAttachmentTemp(tempId, { id: res.data.id, name: res.data.filename || file.name, url: res.data.url, progress: 100, uploading: false, controller: null });
    } catch (err) {
      if (err.name === 'CanceledError' || err.message === 'canceled') {
        // aborted by user
        setAttachments(prev => prev.filter(a => a.tempId !== tempId));
        return;
      }
      replaceAttachmentTemp(tempId, { progress: 0, uploading: false, error: true, controller: null });
      setError(err.response?.data?.error || 'Failed to upload attachment');
    }
  }

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
  function handleFiles(files) {
    const list = Array.from(files || []);
    list.forEach((file, i) => {
      if (file.size > MAX_FILE_SIZE) {
        setError(`File ${file.name} exceeds 25MB limit`);
        return;
      }

      const tempId = `t_${Date.now()}_${i}`;
      const controller = new AbortController();
      const item = { tempId, name: file.name, progress: 0, uploading: true, controller };
      setAttachments(prev => [...prev, item]);
      uploadFile(file, tempId, controller);
    });
  }

  function handleFileInputChange(e) {
    handleFiles(e.target.files);
    e.target.value = null;
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  async function removeAttachment(idOrTempId) {
    // If uploading in-flight, abort the request
    const found = attachments.find(a => a.id === idOrTempId || a.tempId === idOrTempId);
    if (!found) return;

    if (found.uploading && found.controller) {
      try { found.controller.abort(); } catch (e) {}
      setAttachments(prev => prev.filter(a => a.tempId !== found.tempId));
      return;
    }

    // If already uploaded (has id), request server to delete
    if (found.id) {
      try {
        await api.delete(`/api/webmail/${mailboxId}/attachments/${found.id}`);
      } catch (err) {
        // ignore server errors for now
      }
    }

    setAttachments(prev => prev.filter(a => a.id !== idOrTempId && a.tempId !== idOrTempId));
  }

  const [isPendingSend, setIsPendingSend] = React.useState(false);
  const pendingTimeoutRef = React.useRef(null);
  const pendingIntervalRef = React.useRef(null);
  const [countdown, setCountdown] = React.useState(5);

  async function doSend(payload) {
    setIsPendingSend(false);
    setSending(true);
    setError(null);

    try {
      await api.post(`/api/webmail/${mailboxId}/send`, payload);

      // clear draft on success
      try { localStorage.removeItem(draftKey); } catch (e) {}
      onSent && onSent();
      onClose();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to send email';
      setError(message);
    } finally {
      setSending(false);
      setCountdown(5);
      if (pendingIntervalRef.current) {
        clearInterval(pendingIntervalRef.current);
        pendingIntervalRef.current = null;
      }
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
        pendingTimeoutRef.current = null;
      }
    }
  }

  function handleUndoSend() {
    if (pendingIntervalRef.current) {
      clearInterval(pendingIntervalRef.current);
      pendingIntervalRef.current = null;
    }
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    setIsPendingSend(false);
    setCountdown(5);
  }

  function handleClose() {
    // cancel pending send if any
    handleUndoSend();
    onClose();
  }

  function handleDiscard() {
    try { localStorage.removeItem(draftKey); } catch (e) {}
    handleUndoSend();
    onClose();
  }

  function handleSend(e) {
    e.preventDefault();

    if (!formData.to.trim()) {
      setError('Recipient email is required');
      return;
    }

    setError(null);

    // prevent send while attachments are uploading
    if (attachments.some(a => a.uploading)) {
      setError('Please wait for attachments to finish uploading');
      return;
    }

    const payload = {
      to: formData.to.split(',').map(e => e.trim()).filter(e => e),
      cc: formData.cc ? formData.cc.split(',').map(e => e.trim()).filter(e => e) : undefined,
      bcc: formData.bcc ? formData.bcc.split(',').map(e => e.trim()).filter(e => e) : undefined,
      subject: formData.subject,
      bodyText: formData.bodyText,
      attachments: attachments.filter(a => a.id).map(a => a.id),
    };

    // Start pending send with countdown
    setIsPendingSend(true);
    setCountdown(5);

    pendingIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(pendingIntervalRef.current);
          pendingIntervalRef.current = null;
        }
        return prev - 1;
      });
    }, 1000);

    pendingTimeoutRef.current = setTimeout(() => doSend(payload), 5000);
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-slate-900">New Message</h2>
            <span className="text-xs text-slate-500">
              {draftStatus === 'saving' && 'Saving…'}
              {draftStatus === 'saved' && savedAt && `Saved ${new Date(savedAt).toLocaleTimeString()}`}
            </span>
          </div>

          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSend} className="flex-1 overflow-y-auto flex flex-col">
          <div className="p-6 space-y-4 flex-1">
            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* To */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                To <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                name="to"
                value={formData.to}
                onChange={handleChange}
                placeholder="recipient@example.com, another@example.com"
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Separate multiple recipients with commas
              </p>
            </div>

            {/* CC */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                CC
              </label>
              <input
                type="text"
                name="cc"
                value={formData.cc}
                onChange={handleChange}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* BCC */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                BCC
              </label>
              <input
                type="text"
                name="bcc"
                value={formData.bcc}
                onChange={handleChange}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Subject
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-medium"
                placeholder="What is this email about?"
              />
            </div>

            {/* Attachments */}
            <div>
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`mt-2 mb-2 p-3 rounded-lg border-2 text-sm ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-dashed border-slate-200 bg-slate-50'}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click(); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} onChange={handleFileInputChange} type="file" className="hidden" multiple />
                <div className="flex items-center justify-between">
                  <div className="text-slate-600">Drag and drop files here, or click to attach</div>
                  <div className="text-blue-600 font-medium">Attach files</div>
                </div>
              </div>

              {attachments.length > 0 && (
                <div className="space-y-2 mt-3">
                  {attachments.map((a) => (
                    <div key={a.tempId || a.id} className="flex items-center justify-between gap-3 bg-slate-50 p-2 rounded border border-slate-200">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{a.name}</div>
                        <div className="w-full bg-slate-100 rounded-full h-1 mt-1 overflow-hidden">
                          <div className="h-1 bg-blue-600 rounded-full" style={{ width: `${a.progress || 0}%` }} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {a.uploading ? (
                          <>
                            <div className="text-xs text-slate-500">Uploading {a.progress || 0}%</div>
                            <button type="button" onClick={() => removeAttachment(a.tempId)} className="text-slate-500 hover:text-rose-600">Cancel</button>
                          </>
                        ) : (
                          <button type="button" onClick={() => removeAttachment(a.id || a.tempId)} className="text-slate-500 hover:text-rose-600">Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 min-h-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Message
              </label>
              <textarea
                name="bodyText"
                value={formData.bodyText}
                onChange={handleChange}
                className="w-full h-full min-h-[200px] border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-sans"
                placeholder="Write your message here..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleDiscard}
              className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
            >
              Discard
            </button>

            {isPendingSend && !sending ? (
              <>
                <button
                  type="button"
                  disabled
                  className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg shadow-lg shadow-blue-500/30 transition-all flex items-center gap-2 opacity-90"
                >
                  Sending in {countdown}s
                </button>
                <button
                  type="button"
                  onClick={handleUndoSend}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
                >
                  Undo
                </button>
              </>
            ) : (
              <button
                type="submit"
                disabled={sending}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-2 px-6 rounded-lg shadow-lg shadow-blue-500/30 transition-all flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} /> Send Message
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
